import React, { useState } from "react";
import { useParams } from "react-router";
import { WizardStep } from "@sscs/types";
import { Dialog, Toast, Wizard } from "@sscs/ui";
import { getQueryStatus } from "@sscs/utils";
import { SoftwareInfo, Upload } from "Components";
import { DEFAULT_URL_IMPORT_DATA, FILE_SOFTWARE_INFO_DATA } from "Constants";
import { useFileUploadChecks } from "Hooks";
import {
  useLazyCheckUrlImportQuery,
  useLazyValidateUrlImportQuery,
} from "Services";
import { FileSoftwareInfo, UrlImportData, UrlImportForm } from "Types";
import {
  apiErrorToast,
  getFileSoftwareInfoFormSchema,
  getUrlImportFormDefaults,
  urlImportFormSchema,
} from "Utils";
import { useImportFileFromUrlMutation } from "../../../services/files.api";
import Overview from "./Overview";
import styles from "./urlImportWizard.module.scss";

type UrlImportWizardProps = { isOpen: boolean; onClose: () => void };

const UrlImportWizardContent = ({
  onClose,
}: Omit<UrlImportWizardProps, "isOpen">): React.JSX.Element => {
  const { groupId } = useParams();
  const [activeStep, setActiveStep] = useState(0);
  const [urlImportData, setUrlImportData] = useState<UrlImportData>(
    DEFAULT_URL_IMPORT_DATA,
  );
  const [softwareInfoData, setSoftwareInfoData] = useState<FileSoftwareInfo>(
    FILE_SOFTWARE_INFO_DATA,
  );
  // Relying on cached data since validate must have been called during form validation
  const [validateUrl, { data: validatedUrlData }] =
    useLazyValidateUrlImportQuery();
  const [checkUrlImport, urlImportQuery] = useLazyCheckUrlImportQuery();
  const [importFileFromUrl, { isLoading: importingFromUrl }] =
    useImportFileFromUrlMutation();
  const {
    hasSufficientCapacity,
    isOverSizeLimit,
    remainingAfterUpload,
    remainingCapacity,
    sizeLimit,
  } = useFileUploadChecks("FILESTREAM", urlImportQuery.data?.file_size ?? null);
  const checkIsSuccessful =
    hasSufficientCapacity && !isOverSizeLimit && !urlImportQuery.isError;

  const handleNext = () => {
    setActiveStep((previousState) => previousState + 1);
  };

  const handlePrevious = () => {
    setActiveStep((previousState) => previousState - 1);
  };

  const handleImport = async () => {
    if (!groupId) {
      return;
    }

    try {
      const { notify_on_completion, ...software_info } = softwareInfoData;

      await importFileFromUrl({
        notify_on_completion,
        body: {
          ...urlImportData,
          import_string: validatedUrlData?.purl ?? urlImportData.url,
          group_id: groupId,
          software_info,
        },
      }).unwrap();

      Toast.show({
        title: (
          <>
            Importing version <strong>{softwareInfoData.version}</strong>
          </>
        ),
        type: "success",
      });
    } catch (error) {
      apiErrorToast(error, "Could not import version");
    }
  };

  return (
    <Wizard
      title="Import file"
      subtitle="URL or PURL import"
      activeStep={activeStep}
      onStepChange={setActiveStep}
      dialogContentClasses={styles["dialog-content"]}
      steps={
        [
          {
            label: "Provide URL/PURL",
            onNext: async (data: UrlImportForm) => {
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              const { authType, ...payload } = data;

              try {
                const { software_info } = await checkUrlImport({
                  ...payload,
                  url: validatedUrlData?.purl ?? payload.url,
                }).unwrap();
                if (software_info) {
                  setSoftwareInfoData({
                    ...FILE_SOFTWARE_INFO_DATA,
                    ...software_info,
                  });
                }
              } catch {
                // the error will be shown on the next step
              }

              setUrlImportData(payload);
              handleNext();
            },
            onPrevious: handlePrevious,
            formValues: getUrlImportFormDefaults(urlImportData),
            schema: urlImportFormSchema(validateUrl),
            formMode: "onChange",
            component: <Upload.UrlImportForm />,
            nextStep: { label: "Validate P/URL" },
          },
          {
            label: "Review usage",
            nextStep: {
              label: checkIsSuccessful ? undefined : "Close",
            },
            onNext: () => {
              if (checkIsSuccessful) {
                handleNext();
              } else {
                onClose();
              }
            },
            onPrevious: handlePrevious,
            component: (
              <Upload.UrlImportUsage
                queryStatus={getQueryStatus(urlImportQuery)}
                fileSize={urlImportQuery.data?.file_size}
                hasSufficientCapacity={hasSufficientCapacity}
                isOverSizeLimit={isOverSizeLimit}
                remainingAfterImport={remainingAfterUpload}
                remainingQuota={remainingCapacity}
                uploadLimit={sizeLimit}
              />
            ),
          },
          {
            label: "Software information",
            onNext: (data: FileSoftwareInfo) => {
              setSoftwareInfoData(data);
              handleNext();
            },
            onPrevious: handlePrevious,
            schema: getFileSoftwareInfoFormSchema(),
            formValues: softwareInfoData,
            formProps: {
              horizontal: true,
              dense: true,
            },
            component: (
              <SoftwareInfo.Form
                importUrl={urlImportData.url}
                isVersion={false}
              />
            ),
          },
          {
            label: "Overview",
            nextStep: { label: "Import", isLoading: importingFromUrl },
            onNext: async () => {
              await handleImport();
              onClose();
            },
            onPrevious: handlePrevious,
            component: (
              <Overview
                softwareInfo={softwareInfoData}
                urlImportData={{
                  ...urlImportData,
                  url: validatedUrlData?.purl ?? urlImportData.url,
                }}
                fileSize={urlImportQuery.data?.file_size ?? null}
                remainingAfterImport={remainingAfterUpload}
              />
            ),
          },
        ] satisfies [
          WizardStep<UrlImportData>,
          WizardStep,
          WizardStep<FileSoftwareInfo>,
          WizardStep,
        ]
      }
    />
  );
};

const UrlImportWizard = ({
  isOpen,
  ...props
}: UrlImportWizardProps): React.JSX.Element => (
  <Dialog.Frame isOpen={isOpen} onClose={props.onClose} size="md">
    <UrlImportWizardContent {...props} />
  </Dialog.Frame>
);

export default UrlImportWizard;
